import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  ChatBubbleOutline as PromptIcon,
} from "@mui/icons-material";
import DataTable from "../../components/common/DataTable";
import PageHeader from "../../components/common/PageHeader";
import AlertMessage from "../../components/common/AlertMessage";
import LoadingBackdrop from "../../components/common/LoadingBackdrop";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import { useAlert } from "../../context/AlertContext";
import api from "../../services/api";
import { Dataset, TableColumn, Prompt } from "../../types";
import { formatDateTime } from "../../utils/formatters";

const DatasetList: React.FC = () => {
  const navigate = useNavigate();
  const alert = useAlert();

  // State variables
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [prompts, setPrompts] = useState<Prompt[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [selectedDataset, setSelectedDataset] = useState<Dataset | null>(null);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // Fetch datasets and prompts on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch datasets
        const datasetsRes = await api.datasets.getAll();
        setDatasets(datasetsRes.data);

        // Simple fetch of prompts
        try {
          const promptsRes = await api.prompts.getAll();

          if (promptsRes && promptsRes.data && Array.isArray(promptsRes.data)) {
            setPrompts(promptsRes.data);
          } else {
            console.error("Unexpected prompts response format:", promptsRes);
          }
        } catch (promptError) {
          console.error("Error fetching prompts:", promptError);
        }

        setError(null);
      } catch (error) {
        console.error("Error fetching data:", error);
        setError("Failed to load prompt tests. Please try again.");
        alert.error("Failed to fetch prompt tests");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [alert]);

  // Helper function to get prompt name
  const getPromptName = (promptId: string): string => {
    if (!promptId) return "None";
    console.log(`Finding prompt with ID ${promptId} in:`, prompts);
    const prompt = prompts.find((p) => p._id === promptId);
    return prompt ? prompt.name : promptId;
  };

  // Handle view dataset
  const handleView = (dataset: Dataset) => {
    navigate(`/datasets/${dataset._id}`);
  };

  // Handle edit dataset
  const handleEdit = (dataset: Dataset) => {
    navigate(`/datasets/${dataset._id}/edit`);
  };

  // Handle delete dataset
  const handleDelete = (dataset: Dataset) => {
    setSelectedDataset(dataset);
    setDeleteDialogOpen(true);
  };

  // Confirm delete dataset
  const confirmDelete = async () => {
    if (!selectedDataset) return;

    try {
      setIsProcessing(true);
      await api.datasets.delete(selectedDataset._id);
      alert.success("Prompt test deleted successfully");
      setDatasets(datasets.filter((d) => d._id !== selectedDataset._id));
      setDeleteDialogOpen(false);
      setSelectedDataset(null);
    } catch (error) {
      console.error("Error deleting dataset:", error);
      alert.error("Failed to delete prompt test");
    } finally {
      setIsProcessing(false);
    }
  };

  console.log("PROMPTS:", prompts);

  // Table columns configuration
  const columns: TableColumn[] = [
    {
      field: "name",
      headerName: "Name",
      sortable: true,
      align: "left",
      flex: 1,
      minWidth: 200,
    },
    {
      field: "promptId",
      headerName: "Associated Prompt",
      sortable: true,
      align: "left",
      flex: 1,
      minWidth: 200,
      type: "custom",
      render: (row: Dataset) => {
        if (!row.promptId) return <Chip size="small" label="None" />;

        // Try to find prompt name
        let displayName = row.promptId; // Default to ID

        // Find in prompts array
        const prompt = prompts.find((p) => p._id === row.promptId);
        if (prompt && prompt.name) {
          displayName = prompt.name;
        }

        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <PromptIcon fontSize="small" color="primary" />
            <Button
              size="small"
              variant="text"
              color="primary"
              sx={{
                textTransform: "none",
                fontWeight: "medium",
                fontSize: "0.875rem",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/prompts/${row.promptId}`);
              }}
            >
              {displayName}
            </Button>
          </Box>
        );
      },
    },
    {
      field: "variableCount",
      headerName: "Variables",
      sortable: true,
      align: "center",
      width: 120,
      render: (row: Dataset) => {
        const totalVariables = Object.values(row.variables || {}).reduce(
          (sum, vars) => sum + vars.length,
          0,
        );
        return (
          <Chip
            size="small"
            label={totalVariables.toString()}
            color={totalVariables > 0 ? "primary" : "default"}
          />
        );
      },
    },
    {
      field: "createdAt",
      headerName: "Created",
      type: "date",
      sortable: true,
      align: "left",
      width: 160,
      render: (row: Dataset) => formatDateTime(row.createdAt),
    },
    {
      field: "updatedAt",
      headerName: "Last Updated",
      type: "date",
      sortable: true,
      align: "left",
      width: 160,
      render: (row: Dataset) => formatDateTime(row.updatedAt),
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      align: "right",
      width: 120,
      render: (row: Dataset) => (
        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
          <Tooltip title="View">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleView(row);
              }}
            >
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <PageHeader
        title="Prompt Tests"
        subtitle="Manage prompt tests for prompt testing"
        actionPath="/datasets/new"
        actionLabel="New Prompt Test"
        breadcrumbs={[
          { label: "Dashboard", path: "/" },
          { label: "Prompt Tests", path: "/datasets" },
        ]}
      />

      <LoadingBackdrop open={loading && !error && datasets.length === 0} />

      {error && (
        <AlertMessage
          open={Boolean(error)}
          onClose={() => setError(null)}
          severity="error"
          title="Error"
          message={error}
        />
      )}

      <DataTable
        title="All Prompt Tests"
        data={datasets}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onRowClick={handleView}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete Prompt Test"
        message={`Are you sure you want to delete the prompt test "${selectedDataset?.name}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        confirmButtonColor="error"
        onConfirm={confirmDelete}
        onCancel={() => setDeleteDialogOpen(false)}
        loading={isProcessing}
      />
    </Box>
  );
};

export default DatasetList;
